import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import type {
  VariantHistoryDownloadResponse,
  VariantHistoryListResponse,
} from '@/shared/types/variantHistory';
import type { VariantHistoryPeriod } from '@/shared/variantHistoryPeriods';

// The Variant History tab's reads (FIG-239 · UC-256). The snapshot tables are not in the generated
// Supabase types and are admin-only, so everything goes through variant-history-data-v1, which checks
// the caller is an admin on the server and returns stored values only.

const FUNCTION_NAME = 'variant-history-data-v1';

export interface VariantHistoryListParams {
  organisationId: string;
  period: VariantHistoryPeriod;
  /** A single PT day ('YYYY-MM-DD'); when set the period is ignored. */
  date: string;
  page: number;
}

export const variantHistoryQueryKey = (params: VariantHistoryListParams) =>
  ['variant-history', params.organisationId, params.date || params.period, params.page] as const;

export async function fetchVariantHistory(params: VariantHistoryListParams): Promise<VariantHistoryListResponse> {
  const { data } = await invokeFunction<VariantHistoryListResponse>(supabase, FUNCTION_NAME, {
    body: {
      action: 'list',
      organisationId: params.organisationId,
      ...(params.date ? { date: params.date } : { period: params.period }),
      page: params.page,
    },
  });
  // Anything that is not a full list is a failure: the tab never falls back to other figures.
  if (!data || data.success !== true || !Array.isArray(data.rows)) {
    throw new Error('Variant History did not return the stored days');
  }
  return data;
}

export async function fetchVariantHistoryDownload(
  organisationId: string,
  snapshotDate: string,
): Promise<VariantHistoryDownloadResponse> {
  const { data } = await invokeFunction<VariantHistoryDownloadResponse>(supabase, FUNCTION_NAME, {
    body: { action: 'download', organisationId, snapshotDate },
  });
  if (!data || data.success !== true || typeof data.csv !== 'string' || typeof data.fileName !== 'string') {
    throw new Error('Variant History did not return the file');
  }
  return data;
}

export function useVariantHistory(params: VariantHistoryListParams) {
  return useQuery({
    queryKey: variantHistoryQueryKey(params),
    queryFn: () => fetchVariantHistory(params),
    enabled: Boolean(params.organisationId),
    // Stored days never change, but a day is added each morning: re-read when the tab is opened again.
    staleTime: 30_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}
